<script>
  import { onDestroy, onMount } from 'svelte';
  import { useNavigate } from 'svelte-navigator';
  import debounce from 'lodash/debounce';
  import isEqual from 'lodash/isEqual';
  import noop from 'lodash/noop';

  import { Button, DataTable, InlineNotification, Link, Toolbar, ToolbarContent } from 'carbon-components-svelte';

  import Launch16 from 'carbon-icons-svelte/lib/Launch16';

  import LoadingSpinner from '../../../components/LoadingSpinner.svelte';
  import ToolbarSearchWithRouting from '../../../components/ToolbarSearchWithRouting.svelte';
  import PaginationWithRouting from '../../../components/PaginationWithRouting.svelte';
  import RelativeDate from '../../../components/RelativeDate.svelte';
  import StatusIcon from '../../../components/StatusIcon.svelte';

  import { getRestoreRequests } from '../../../services';
  import { isUrl, parseSortEvent, getUrlLabel } from '../../../utils';

  const navigate = useNavigate();

  const HEADER_KEY_TO_ORDER_BY = {
    createdByDisplayName: 'creator.name',
  };

  const tableHeaders = [
    { key: 'ticketId', value: 'Ticket', sort: noop },
    { key: 'status', value: 'Status', sort: noop },
    { key: 'createdByDisplayName', value: 'Requested By', sort: noop },
    { key: 'createdAt', value: 'Requested', sort: noop },
    { key: 'actions', value: '', empty: true, sort: false },
  ];
  const statusToIcon = {
    Pending: undefined,
    Approved: true,
    Rejected: false,
    Cancelled: false,
  };

  let requestedRestores = [],
    requestedRestoresCount = 0,
    runningTasks = 0,
    loading = true,
    searchTextInitialized = false,
    paginationInitialized = false;

  const criteria = {
    searchText: '',
    currentPage: 1,
    pageSize: 25,
    orderBy: null,
    orderDirection: null,
  };
  let prevCriteria = {};

  async function fetchRestoreHistory({ searchText, currentPage, pageSize, orderBy, orderDirection }) {
    runningTasks++;
    try {
      const { items, count } = await getRestoreRequests({
        search: searchText || undefined,
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
        orderBy: orderBy || undefined,
        orderDirection: orderDirection || undefined,
      });
      requestedRestores = items;
      requestedRestoresCount = count;
    } catch (error) {
      console.error('[RestoreHistory] Failed to load restore history!', error);
    } finally {
      runningTasks--;
    }
  }

  const fetchRestoreHistoryDebounced = debounce(fetchRestoreHistory, 300);

  function onSearchTextInit() {
    searchTextInitialized = true;
  }

  function onPaginationInit() {
    paginationInitialized = true;
  }

  function onSearchTextChange() {
    if (criteria.currentPage !== 1) {
      criteria.currentPage = 1;
    }
  }

  const onSortChange = (event) =>
    parseSortEvent(event, ({ orderBy, orderDirection }) => {
      criteria.orderBy = orderBy ? HEADER_KEY_TO_ORDER_BY[orderBy] ?? orderBy : null;
      criteria.orderDirection = orderDirection;
    });

  function goToPage(event, path) {
    event.preventDefault();
    navigate(path);
  }

  onMount(() => {
    fetchRestoreHistory(criteria);
    prevCriteria = { ...criteria };
  });

  onDestroy(() => fetchRestoreHistoryDebounced.cancel());

  $: if (searchTextInitialized && paginationInitialized && !isEqual(criteria, prevCriteria)) {
    const initialized = Object.keys(prevCriteria).length > 0;
    const searchChanged = prevCriteria.searchText !== criteria.searchText;
    const clearedSearch = prevCriteria.searchText && !criteria.searchText;

    if (!initialized || !searchChanged) {
      fetchRestoreHistory(criteria);
    } else {
      if (clearedSearch) {
        loading = true;
      }
      fetchRestoreHistoryDebounced(criteria);
    }

    prevCriteria = { ...criteria };
  }

  $: loading = runningTasks > 0;
  $: showTable = loading || !!criteria.searchText || !!requestedRestores.length;
</script>

<LoadingSpinner {loading}>
  {#if showTable}
    <div class="table-wrapper">
      <DataTable class="mst-table--lock-end" headers={tableHeaders} rows={requestedRestores} sortable on:click:header={onSortChange}>
        <span slot="cell" let:row let:cell>
          {#if cell.key === 'ticketId'}
            {#if isUrl(cell.value)}
              <div class="ticket">
                <strong>{getUrlLabel(cell.value)}</strong>
                <Button
                  icon={Launch16}
                  iconDescription="Open ticket in new tab"
                  tooltipPosition="right"
                  kind="ghost"
                  size="small"
                  href={cell.value}
                  target="_blank"
                  rel="noopener noreferrer"
                />
              </div>
            {:else}
              <strong>{cell.value}</strong>
            {/if}
          {:else if cell.key === 'status'}
            <div class="status">
              <StatusIcon valid={statusToIcon[row.status]} />
              <em>{row.status}</em>
            </div>
          {:else if cell.key === 'createdByDisplayName'}
            <Link href={`/user/${row.createdBy}`} on:click={(event) => goToPage(event, `/user/${row.createdBy}`)}>
              {row.createdByDisplayName}
            </Link>
          {:else if cell.key === 'actions'}
            <Link href={`/object-restore-request/${row.id}`} on:click={(event) => goToPage(event, `/object-restore-request/${row.id}`)}>
              View Request
            </Link>
          {:else if cell.key === 'createdAt'}
            <RelativeDate date={cell.value} />
          {:else}
            {cell.display ? cell.display(cell.value, row) : cell.value ?? ''}
          {/if}
        </span>
        <Toolbar class="table-toolbar">
          <ToolbarContent>
            <ToolbarSearchWithRouting
              placeholder="Search requests..."
              persistent
              bind:searchText={criteria.searchText}
              on:toolbarSearchValueChange={onSearchTextChange}
              on:toolbarSearchValueInit={onSearchTextInit}
            />
            <Button on:click={() => navigate('/object-restore-request/create')}>New Request</Button>
          </ToolbarContent>
        </Toolbar>
      </DataTable>
      <PaginationWithRouting
        bind:page={criteria.currentPage}
        bind:pageSize={criteria.pageSize}
        pageSizes={[5, 10, 25, 50]}
        totalItems={requestedRestoresCount}
        on:init={onPaginationInit}
      />
    </div>
  {:else}
    <InlineNotification lowContrast kind="info" subtitle="No requested restores." hideCloseButton />
  {/if}
</LoadingSpinner>

<style>
  .ticket {
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }

  .ticket :global(.bx--btn--ghost.bx--btn--icon-only .bx--btn__icon path:not([data-icon-path]):not([fill=none])) {
    fill: var(--cds-link-01)!important;
  }

  .status {
    display: flex;
    align-items: center;
  }

  .status :global(em) {
    margin-left: 0.25rem;
    font-size: 0.75rem;
  }

  .table-wrapper :global(td:last-child) {
    white-space: nowrap;
  }
</style>
